import React from 'react';
import { Alert } from '@ver-uds/uswds-react';
import { Button, ButtonGroup } from '@ver-uds/react';
import { useAuthState, useLogout } from '@vis-auth/react';
import { AccountType } from 'eve-account-client';
import { useHistory } from 'react-router-dom';
import { EveRoutes } from '@ver-uds/e-verify';
import { useMouDocumentQuery, useCombinedMouTosDocumentQuery } from '../../../services/AccountApi';
import EVerifyEmail from '../EVerifyEmail';
import { useEnrollment } from '../../../context/Enrollment/EnrollmentContext';
import AuthFileLink from '../../AuthFileLink';
import { shouldEvePlusToSBeHidden } from '../../../pages/Enrollment/EnrollmentData';
import { useToggles } from '../../../context/Toggles/TogglesContext';

export interface EnrollmentPendingProps {
  accountTypeText: string;
}

const EnrollmentSuccess = ({ accountTypeText }: EnrollmentPendingProps): JSX.Element => {
  const logout = useLogout();
  const history = useHistory();
  const { user } = useAuthState();

  const toggles = useToggles();
  const isMfaEnvironment = toggles?.get('MFA_USER_CREATION') === 'on';

  const { completedEnrollment } = useEnrollment();
  const companyName = completedEnrollment?.companyInfo?.companyName || 'account';
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id ?? (completedEnrollment?.id as string);

  if (isMfaEnvironment && completedEnrollment?.type !== AccountType.CHILD) {
    const currentTime = Date.now();
    localStorage.setItem('EnrollmentCompletedTime', JSON.stringify(currentTime));
  }
  const hideEvePlusToS = shouldEvePlusToSBeHidden(
    completedEnrollment?.webServiceAccess?.enabled,
    completedEnrollment?.type,
    completedEnrollment?.companyInfo?.employerCategory?.category,
  );
  const documentTitleText = hideEvePlusToS ? 'View Memorandum of Understanding' : 'Download Terms and Conditions';
  const documentHelpText = hideEvePlusToS
    ? 'If you have trouble viewing the MOU, make sure you have disabled any pop-up blockers and are using the latest version of your Portable Document Format (PDF) viewer software'
    : 'If you have trouble viewing the documents, make sure you are using the latest version of your Portable Document Format (PDF) viewer software';

  const successContext =
    completedEnrollment?.type === AccountType.CHILD
      ? {
          buttonText: 'Manage Company Locations',
          onClickFn: () => history.push(EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW),
        }
      : {
          buttonText: 'Log out of E-Verify',
          onClickFn: () => logout(),
        };
  return (
    <div className="tablet:grid-col-12 desktop:grid-col-9" data-testid="enrollment-success-context">
      <h1>Enrollment Completed</h1>
      <Alert className="margin-y-3" status="success" slim>
        <Alert.Text>You have successfully enrolled your company in E-Verify.</Alert.Text>
      </Alert>
      {!isMfaEnvironment && completedEnrollment?.type !== AccountType.CORPORATE && (
        <p className="margin-y-3">
          Your E-Verify {accountTypeText} administrators will receive their user IDs and passwords by email. Most people
          receive our confirmation email within a few minutes. Please check email inboxes as well as spam or junk mail
          folders because sometimes our emails are mistakenly marked as spam.
        </p>
      )}
      {isMfaEnvironment && completedEnrollment?.type !== AccountType.CORPORATE && (
        <p className="margin-y-3">
          Your E-Verify {accountTypeText} administrator will receive an email with sign-in directions. Most people
          receive the email within a few minutes. Please check email inboxes as well as spam or junk mail folders
          because sometimes our emails are mistakenly marked as spam.
        </p>
      )}
      {!isMfaEnvironment && completedEnrollment?.type === AccountType.CORPORATE && (
        <p className="margin-y-3">
          Your E-Verify {accountTypeText} administrator will receive their user ID and password by email. Most people
          receive our confirmation email within a few minutes. Please check email inboxes as well as spam or junk mail
          folders because sometimes our emails are mistakenly marked as spam.
        </p>
      )}
      {isMfaEnvironment && completedEnrollment?.type === AccountType.CORPORATE && (
        <p className="margin-y-3">
          Your E-Verify {accountTypeText} administrator will receive an email with sign-in directions. Most people
          receive the email within a few minutes. Please check email inboxes as well as spam or junk mail folders
          because sometimes our emails are mistakenly marked as spam.
        </p>
      )}
      {completedEnrollment?.type !== AccountType.CHILD && (
        <p className="margin-y-3">
          If the email is not received within 48 hours, contact E-Verify support at <EVerifyEmail /> for assistance.{' '}
          <b>
            Do not enroll your company again in E-Verify. If you attempt to re-enroll, your enrollment may be delayed.
          </b>
        </p>
      )}
      {completedEnrollment?.type !== AccountType.CORPORATE && (
        <p className="margin-y-3">
          Before you go, click on &quot;{documentTitleText}&quot; and print or save a copy to share with your Human
          Resources manager, legal counsel, and other appropriate staff. {documentHelpText}. Thanks for signing up. Your
          participation is vital in ensuring a legal U.S. workforce.
        </p>
      )}
      {completedEnrollment?.type === AccountType.CHILD && (
        <p className="margin-y-3">
          REMINDER: You must print and provide each of your company locations with the English and Spanish versions of
          the E-Verify Participation poster and the Right to Work poster. These posters are available from the Resources
          section. Your company location(s) must display the posters in plain view at their hiring site(s) to inform
          prospective employees that their company is participating in E-Verify.
        </p>
      )}
      <p className="margin-y-3">
        If you ever have any questions, we&apos;re here to help! Email E-Verify support at <EVerifyEmail />.
      </p>
      {completedEnrollment?.type !== AccountType.CORPORATE ? (
        <ButtonGroup>
          {!hideEvePlusToS ? (
            <AuthFileLink
              linkText="Download Terms and Conditions"
              query={useCombinedMouTosDocumentQuery}
              queryArg={accountId}
              aria-label={`Download Terms and Conditions for ${companyName} in a new tab`}
              className="usa-button usa-button--outline"
            />
          ) : (
            <AuthFileLink
              linkText="View Memorandum of Understanding"
              query={useMouDocumentQuery}
              queryArg={accountId}
              aria-label={`View Memorandum of Understanding for ${companyName} in a new tab`}
              className="usa-button usa-button--outline"
            />
          )}
          <Button type="button" variant="primary" onClick={successContext.onClickFn}>
            {successContext.buttonText}
          </Button>
        </ButtonGroup>
      ) : (
        <Button type="button" variant="primary" onClick={logout}>
          Log out of E-Verify
        </Button>
      )}
    </div>
  );
};

export default EnrollmentSuccess;
